<template>
  <v-expansion-panels v-model="defaultOpenPanelIndex" class="content-add-edit-expansion-panels" flat>
    <v-expansion-panel active-class="expanded" class="mt-0" :disabled="isMapBorderEnabled">
      <v-expansion-panel-header>
        <PtrIcon class="expansion-panel-header-icon" icon="information" />
        <div class="expansion-panel-header-text">{{ $t("contents.mapDesigner.basic-information") }}</div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-form ref="buildingForm" v-model="isFormValid" class="mt-2">
          <v-row>
            <v-col class="py-1">
              <v-text-field
                id="building-title"
                v-model="buildingTitle"
                :rules="[rules.required, isTitleValid]"
                :label="$t(`${translationPath}building-name`)"
                outlined
                hide-details="auto"
                dense
                :disabled="isReadOnly"
                @keydown="setFormDirty"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1 pb-2">
              <v-text-field
                id="building-external-id"
                v-model="eid"
                :rules="[isExternalIdentifierValid]"
                :label="$t(`${translationPath}eid`)"
                outlined
                hide-details="auto"
                dense
                :disabled="isReadOnly"
                @keydown="setFormDirty"
              />
            </v-col>
          </v-row>
          <GeometrySection
            v-if="!isReadOnly"
            ref="geometrySection"
            :feature-id="featureId"
            :should-show-polygon-icon="true"
            :should-show-point-icon="false"
            :is-edit="isEdit"
            @clear="setFormDirty"
          ></GeometrySection>
        </v-form>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel active-class="expanded" class="mt-0" :disabled="isMapBorderEnabled">
      <v-expansion-panel-header>
        <PtrIcon class="expansion-panel-header-icon" icon="custom-integration" />
        <div class="expansion-panel-header-text">{{ $t("contents.mapDesigner.custom-integration") }}</div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <CustomIntegration
          ref="customIntegration"
          :extra-data-prop="extraData"
          :feature-id="featureId?.toString()"
          :is-read-only="isReadOnly"
          @integrationUpdated="setFormDirty"
          @setFormDirty="setFormDirty"
        ></CustomIntegration>
      </v-expansion-panel-content>
      <v-expansion-panel-content></v-expansion-panel-content>
    </v-expansion-panel>
    <div class="v-expansion-panel">
      <div class="v-expansion-panel-header" @click="onSettingsClicked">
        <PtrIcon class="expansion-panel-header-icon" icon="settings" />
        <div class="expansion-panel-header-text">{{ $t(`${translationPath}settings`) }}</div>
      </div>
    </div>
    <slot name="danger-zone"></slot>
    <AdvancedSdkConfigs
      v-if="shouldDisplaySettingsPopup"
      :is-read-only="isReadOnly"
      @discard="(isFormDirty) => onBuildingSettingsDiscard(isFormDirty)"
    />
  </v-expansion-panels>
</template>
<script>
import { mapState, mapGetters } from "vuex";
import PtrIcon from "@/components/shared/PtrIcon";
import CustomIntegration from "@/components/shared/CustomIntegration.vue";
import GeometrySection from "@/components/mapDesigner/GeometrySection.vue";
import AdvancedSdkConfigs from "../views/content/AdvancedSdkConfigs.vue";
import BuildingService from "@/services/BuildingService";
import SdkConfigurationService from "@/services/SdkConfigurationService";
import ValidationHelpers from "@/helpers/ValidationHelpers";
import MapModeConstants from "@/constants/mapModeConstants";
import MapHelpers from "@/helpers/MapHelpers";
import ToastHelpers from "@/helpers/ToastHelpers";
import booleanWithin from "@turf/boolean-within";
import { polygon } from "@turf/helpers";

export default {
  name: "BuildingAddEditView",
  components: { PtrIcon, CustomIntegration, GeometrySection, AdvancedSdkConfigs },
  props: {
    featureId: [String, Number],
    isReadOnly: { type: Boolean, default: false }
  },
  data: () => ({
    translationPath: "contents.buildings.",
    isEdit: false,
    isFormValid: false,
    rules: {
      required: (value) => ValidationHelpers.isRequired(value),
      coordinates: (value) => ValidationHelpers.isCoordinateValid(value)
    },
    building: {},
    buildingTitle: "",
    eid: "",
    confirmDialog: undefined,
    showDiscardChangesModal: false,
    activateBrowserAlert: false,
    defaultOpenPanelIndex: 0,
    extraData: {},
    shouldDisplaySettingsPopup: false
  }),
  computed: {
    drawnCoordinates: {
      get() {
        return this.$store.state.MAP.drawnCoordinates;
      },
      set(value) {
        this.$store.commit("MAP/DRAWN_COORDINATES", value);
      }
    },
    ...mapState("MAP", [
      "mapDraw",
      "isMapReady",
      "currentClient",
      "currentSite",
      "currentBuilding",
      "isMapBorderEnabled"
    ]),
    ...mapState("CONTENT", ["isFormDirty", "buildings"]),
    ...mapState("ADVANCED_CONFIGS", ["initialSdkConfigurations", "cachedSdkConfigurations"]),
    ...mapGetters("MAP", ["currentSiteObject"])
  },
  watch: {
    $route() {
      this.setMapUI();
      this.$store.dispatch("MAP/DISPLAY_SITE_BORDERS");
    },
    featureId: {
      immediate: true,
      async handler() {
        this.$store.commit("CONTENT/IS_FORM_DIRTY", false);
        this.$store.dispatch("MAP/DRAWN_POLYGON_CHANGED", { feature: undefined, shouldKeepFormClean: true });
        this.$store.commit("MAP/DRAWN_COORDINATES", undefined);
        const building = this.buildings.find(
          (building) => building.buildingInternalIdentifier.toString() === this.featureId?.toString()
        );
        if (building) {
          this.isEdit = true;
          this.$store.dispatch("MAP/SET_CURRENT_BUILDING", { building: this.featureId });
          this.buildingTitle = building.buildingTitle;
          this.extraData = building.buildingExtraData;
          this.eid = building.buildingExternalIdentifier;
          this.$store.commit("MAP/DRAWN_COORDINATES", building.geometry.coordinates);
          let feature = {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: []
            }
          };
          if (this.mapDraw.getAll().features.length > 0) {
            feature = this.mapDraw.getAll().features[0];
          }
          feature.geometry.coordinates = building.geometry.coordinates;
          this.$store.dispatch("MAP/DRAWN_POLYGON_CHANGED", { feature, shouldKeepFormClean: true });
          const sdkConfigResponse = await SdkConfigurationService.getBuildingSdkConfigurations(this.featureId);
          this.$store.commit("ADVANCED_CONFIGS/INITIAL_SDK_CONFIGURATIONS", JSON.stringify(sdkConfigResponse));
          this.$store.commit("ADVANCED_CONFIGS/CACHED_SDK_CONFIGURATIONS", sdkConfigResponse);
        } else if (this.featureId === "add") {
          this.$refs.buildingForm?.resetValidation();
          this.isEdit = false;
          this.buildingTitle = "";
          this.eid = "";
          this.extraData = {};
        }
      }
    },
    drawnCoordinates() {
      this.emitValidationStatusForForm();
    },
    isFormValid() {
      this.emitValidationStatusForForm();
    },
    isMapBorderEnabled() {
      this.emitValidationStatusForForm();
    }
  },
  created() {
    MapHelpers.enableMapInteractions();
    this.setMapUI();
  },
  beforeDestroy() {
    MapHelpers.disableMapInteractions();
    this.$store.commit("MAP/MAP_MODE", MapModeConstants.EMPTY_MODE);
    this.$store.dispatch("MAP/DRAWN_POLYGON_CHANGED", { feature: undefined, shouldKeepFormClean: true });
    this.$store.commit("CONTENT/IS_FORM_DIRTY", false);
  },
  methods: {
    setMapUI() {
      this.$store.commit("MAP/SHOW_UI_ICON", { iconName: "target" });
      this.$store.commit("MAP/SHOW_UI_ICON", { iconName: "zoom" });
    },
    goBack() {
      this.$router
        .push({ name: "Buildings", params: { buildingId: this.currentBuilding } })
        .catch((e) => console.log(e.message));
    },
    isBuildingWithinSite() {
      let coordinates;
      try {
        coordinates = JSON.parse(this.drawnCoordinates || "[]");
      } catch (e) {
        coordinates = this.drawnCoordinates;
      }
      let turfBuildingPolygon;
      try {
        turfBuildingPolygon = polygon(coordinates);
      } catch (e) {
        return false;
      }
      const turfSitePolygon = polygon(this.currentSiteObject.geometry.coordinates);
      const isBuildingWithinSite = booleanWithin(turfBuildingPolygon, turfSitePolygon);
      return isBuildingWithinSite;
    },
    async save() {
      if (!this.isSinglePolygon()) {
        ToastHelpers.createErrorToast(this.$t(this.translationPath + "building-divide-error"));
        return false;
      }
      if (!this.isBuildingWithinSite()) {
        ToastHelpers.createErrorToast(this.$t(this.translationPath + "buildings-within-site-error"));
        return false;
      }
      let coordinates;
      try {
        coordinates = JSON.parse(this.drawnCoordinates || "[]");
      } catch (e) {
        coordinates = this.drawnCoordinates;
      }
      let extraData = this.$refs?.customIntegration?.getExtraData();
      if (!extraData && this.feature) {
        extraData = this.feature.properties?.extra || this.feature.properties?.extraData;
      }
      const request = {
        buildingTitle: this.buildingTitle.trim(),
        buildingExternalIdentifier: this.eid.trim(),
        buildingExtraData: extraData,
        geometry: {
          type: "Polygon",
          coordinates
        }
      };
      let response;
      if (this.isEdit) {
        response = await BuildingService.updateBuilding(request, this.currentBuilding);
      } else {
        response = await BuildingService.createBuilding(request, this.currentSite);
      }
      if (response?.createdTimestampUtcEpochSeconds) {
        await this.saveSdkConfigurations(
          this.isEdit ? this.currentBuilding : response.result.buildingInternalIdentifier
        );
        this.$store.commit("CONTENT/IS_FORM_DIRTY", false);
        this.$router
          .push({
            name: "Buildings",
            params: { buildingId: this.isEdit ? this.currentBuilding : response.result.buildingInternalIdentifier }
          })
          .catch((e) => console.log(e.message));
        return true;
      }
      return false;
    },
    async saveSdkConfigurations(buildingId) {
      if (!this.isFormDirty) {
        return;
      }
      if (this.initialSdkConfigurations === JSON.stringify(this.cachedSdkConfigurations)) {
        console.debug("SDK configurations not changed");
        return;
      }
      if (!this.isEdit && JSON.stringify(this.cachedSdkConfigurations) === "[]") {
        // Skip config request on add if sdk config is empty
        return;
      }
      await SdkConfigurationService.createBuildingSdkConfigurations(
        buildingId,
        this.buildingTitle,
        this.cachedSdkConfigurations
      );
    },
    isTitleValid(value) {
      return ValidationHelpers.isBuildingTitleValid(value) || this.$t(`${this.translationPath}invalid-title`);
    },
    isExternalIdentifierValid(value) {
      return (
        ValidationHelpers.isOptionalExternalIdentifierValid(value) ||
        this.$t("contents.validations.invalid-optional-external-identifier")
      );
    },
    async deleteContent() {
      const response = await BuildingService.deleteBuilding(this.$route.params.buildingId);
      if (response?.createdTimestampUtcEpochSeconds) {
        await this.$store.dispatch("CONTENT/SET_BUILDINGS", { forceUpdate: true, siteId: this.currentSite });
        return true;
      }
      return false;
    },
    isSinglePolygon() {
      return this.mapDraw?.getAll()?.features?.[0]?.geometry?.type !== "MultiPolygon";
    },
    setFormDirty() {
      this.$store.commit("CONTENT/IS_FORM_DIRTY", true);
    },
    emitValidationStatusForForm() {
      let isValid =
        this.isFormValid && this.drawnCoordinates && this.drawnCoordinates?.length !== 0 && !this.isMapBorderEnabled;
      this.$emit("valid", isValid);
    },
    onSettingsClicked() {
      this.shouldDisplaySettingsPopup = true;
    },
    onBuildingSettingsDiscard() {
      this.shouldDisplaySettingsPopup = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.v-expansion-panel {
  cursor: pointer;
}
</style>
